/* eslint '@typescript-eslint/member-ordering': [
      'warn',
      {
        default: {
          order: 'alphabetically-case-insensitive',
        },
      },
    ], */
import * as editorCapabilitiesConstants from './constants';
import type {
  FormattingTrayOptions,
  ToolbarLayout,
} from './toolbarCustomization';
import type { DocumentsOptions } from '../commands/documents/InsertDocuments';
import type { MeetingsOptions } from '../commands/meetings/InsertMeetings';
import type { InvoicesOptions } from '../commands/invoices/InsertInvoices';
import type { KnowledgeArticleOptions } from '../commands/knowledgeArticles/InsertKnowledgeArticle';
import type { QuotesOptions } from '../commands/quotes/InsertQuotes';
import type { SnippetsOptions } from '../commands/snippets/InsertSnippet';
import type { TemplatesOptions } from '../commands/templates/InsertTemplates';
import type { PaymentLinkOptions } from '../commands/paymentLink/InsertPaymentLink';
import type { VideoInsertionOptions } from '../commands/InsertVideo';
import type { InlineImageOptions } from '../commands/InlineImage';
import type { RenderTrayOptions } from '../commands/RenderTray';
import type { FileAttachmentsOptions } from '../commands/FileAttachments';
import type { EmojiOptions } from '../commands/InsertEmoji';
import type { LinkOptions } from '../commands/InsertLink';
import type { PersonalizationTokenOptions } from '../commands/InsertPersonalizationToken';
import type { PersonalizationTokenInlineOptions } from '../plugins/personalization-token-inline';
import type { PasteOverrideOptions } from '../plugins/PasteOverridePlugin';
import type { GlobalVarsOptions } from '../utils/globalVarUtil';
import type { TrackingOptions } from '../utils/configUtils';
import type { MacrosOptions } from '../components/MacrosMenu';
import type { VideoConferenceLinkOptions } from '../commands/VideoConferenceButton';
import type { AiAssistantOptions } from '../commands/ai/AIAssistant';
import type { ContentAssistantOptions } from '../commands/ai/ContentAssistant';
import type {
  ContentAssistantInlineMenuOptions,
  ContentAssistantSlashCommandOptions,
  CustomInsertCapability,
  CustomInsertCapabilityOptions,
  KeyMapOptions,
  SignatureOptions,
} from '../../types';

/** Union of all built-in insert capability constants */
export type DefaultInsertCapability =
  | typeof editorCapabilitiesConstants.DOCUMENTS
  | typeof editorCapabilitiesConstants.MEETINGS
  | typeof editorCapabilitiesConstants.SNIPPETS
  | typeof editorCapabilitiesConstants.QUOTES
  | typeof editorCapabilitiesConstants.KNOWLEDGE_ARTICLE
  | typeof editorCapabilitiesConstants.VIDEO_INSERTION
  | typeof editorCapabilitiesConstants.TEMPLATES
  | typeof editorCapabilitiesConstants.PAYMENT_LINK
  | typeof editorCapabilitiesConstants.INVOICES
  | typeof editorCapabilitiesConstants.SIGNATURE;

/** Union of all insert capability types (built-in and custom) */
export type InsertCapability = DefaultInsertCapability | CustomInsertCapability;

/**
 * Type guard to check if a capability is a custom insert capability.
 * Custom capabilities are identified by the "CUSTOM_" prefix.
 */
export function isCustomCapability(
  capability: unknown
): capability is CustomInsertCapability {
  return typeof capability === 'string' && capability.startsWith('CUSTOM_');
}

/**
 * Type guard to check if a capability is any type of insert capability
 * (either built-in or custom).
 */
export function isInsertCapability(
  capability: unknown
): capability is InsertCapability {
  return (
    capability === editorCapabilitiesConstants.DOCUMENTS ||
    capability === editorCapabilitiesConstants.MEETINGS ||
    capability === editorCapabilitiesConstants.SNIPPETS ||
    capability === editorCapabilitiesConstants.QUOTES ||
    capability === editorCapabilitiesConstants.KNOWLEDGE_ARTICLE ||
    capability === editorCapabilitiesConstants.VIDEO_INSERTION ||
    capability === editorCapabilitiesConstants.TEMPLATES ||
    capability === editorCapabilitiesConstants.PAYMENT_LINK ||
    capability === editorCapabilitiesConstants.INVOICES ||
    capability === editorCapabilitiesConstants.SIGNATURE ||
    isCustomCapability(capability)
  );
}

// =============================================================================
// Capability Options Types
// =============================================================================

/** Empty configuration type for capabilities that have no options */
type NoOptions = {};

type NoopAiSummaryOptions = {
  globalSettingEnabled?: boolean;
  onSummaryGenerated?: VoidFunction;
  onSummaryRequested?: VoidFunction;
  threadId?: string;
};

// =============================================================================
// Main EditorConfig Type
// =============================================================================

export default editorCapabilitiesConstants;

/** Union of all editor capability constant values */
export type EditorCapability =
  (typeof editorCapabilitiesConstants)[keyof typeof editorCapabilitiesConstants];

/**
 * Main configuration object for the ProseMirror editor.
 * Each key corresponds to a capability that can be enabled and configured.
 * Omitting a key disables that capability.
 */
export type EditorConfig = {
  [editorCapabilitiesConstants.AI_ASSISTANT]?: AiAssistantOptions;
  [editorCapabilitiesConstants.AI_SUMMARY]?: NoopAiSummaryOptions;
  [editorCapabilitiesConstants.BLOCKQUOTE]?: NoOptions;
  [editorCapabilitiesConstants.BOLD]?: NoOptions;
  [editorCapabilitiesConstants.CLEAR_FORMATTING]?: NoOptions;
  [editorCapabilitiesConstants.CONTENT_ASSISTANT]?: ContentAssistantOptions;
  [editorCapabilitiesConstants.CONTENT_ASSISTANT_INLINE_MENU]?: ContentAssistantInlineMenuOptions;
  [editorCapabilitiesConstants.CONTENT_ASSISTANT_SLASH_COMMAND]?: ContentAssistantSlashCommandOptions;
  [editorCapabilitiesConstants.DOCUMENTS]?: DocumentsOptions;
  [editorCapabilitiesConstants.DRAFT_NOT_SAVED]?: NoOptions;
  [editorCapabilitiesConstants.EMOJI]?: EmojiOptions;
  [editorCapabilitiesConstants.FILE_ATTACHMENTS]?: FileAttachmentsOptions;
  [editorCapabilitiesConstants.FILE_DROP]?: NoOptions;
  [editorCapabilitiesConstants.FONT_SIZE]?: NoOptions;
  [editorCapabilitiesConstants.FONT_STYLE]?: NoOptions;
  [editorCapabilitiesConstants.FORMATTING_TRAY]?: FormattingTrayOptions;
  [editorCapabilitiesConstants.GLOBAL_VARS]?: GlobalVarsOptions;
  [editorCapabilitiesConstants.HORIZONTAL_RULE]?: NoOptions;
  [editorCapabilitiesConstants.INLINE_EDITOR_AI]?: NoOptions;
  [editorCapabilitiesConstants.INLINE_IMAGE]?: InlineImageOptions;
  [editorCapabilitiesConstants.INVOICES]?: InvoicesOptions;
  [editorCapabilitiesConstants.ITALIC]?: NoOptions;
  [editorCapabilitiesConstants.KEYMAP]?: KeyMapOptions;
  [editorCapabilitiesConstants.KNOWLEDGE_ARTICLE]?: KnowledgeArticleOptions;
  [editorCapabilitiesConstants.LINK]?: LinkOptions;
  [editorCapabilitiesConstants.LISTS]?: NoOptions;
  [editorCapabilitiesConstants.MACROS]?: MacrosOptions;
  [editorCapabilitiesConstants.MEETINGS]?: MeetingsOptions;
  [editorCapabilitiesConstants.PASTE_OVERRIDE]?: PasteOverrideOptions;
  [editorCapabilitiesConstants.PAYMENT_LINK]?: PaymentLinkOptions;
  [editorCapabilitiesConstants.PERSONALIZATION_TOKEN]?: PersonalizationTokenOptions;
  [editorCapabilitiesConstants.PERSONALIZATION_TOKEN_INLINE]?: PersonalizationTokenInlineOptions;
  [editorCapabilitiesConstants.QUOTES]?: QuotesOptions;
  [editorCapabilitiesConstants.RENDER_SUBMIT_BUTTON]?: NoOptions;
  [editorCapabilitiesConstants.RENDER_TRAY]?: RenderTrayOptions;
  [editorCapabilitiesConstants.SIGNATURE]?: SignatureOptions;
  [editorCapabilitiesConstants.SNIPPETS]?: SnippetsOptions;
  [editorCapabilitiesConstants.STRIKETHROUGH]?: NoOptions;
  [editorCapabilitiesConstants.TEMPLATES]?: TemplatesOptions;
  [editorCapabilitiesConstants.TEXT_ALIGN]?: NoOptions;
  [editorCapabilitiesConstants.TEXT_COLOR]?: NoOptions;
  [editorCapabilitiesConstants.TEXT_HIGHLIGHT]?: NoOptions;
  [editorCapabilitiesConstants.TOOLBAR_CUSTOMIZATION]?: ToolbarLayout;
  [editorCapabilitiesConstants.TRACKING]?: TrackingOptions;
  [editorCapabilitiesConstants.UNDERLINE]?: NoOptions;
  [editorCapabilitiesConstants.VIDEO_CONFERENCE_LINK]?: VideoConferenceLinkOptions;
  [editorCapabilitiesConstants.VIDEO_INSERTION]?: VideoInsertionOptions;
  [key: CustomInsertCapability]: CustomInsertCapabilityOptions;
};
